import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import EL_StoresARD from './EL_StoresARD';
import CustomAlert from '../../Common/CustomAlert';
import EL_ManageStoresPurchases from './EL_ManageStoresPurchases';
import ImportVouchersPurchased from './ImportVouchersPurchased';
import alert_A_bg from '../../Common/Images/ImagesForAlert/alert_A_bg.png';
import alert_bg from '../../Common/Images/ImagesForAlert/alert_Q_bg_small.png';
import ok_n from '../../Common/Images/ImagesForAlert/ok_n.png';
import ok_o from '../../Common/Images/ImagesForAlert/ok_o.png';
import yes_n from '../../Common/Images/ImagesForAlert/yes_n.png';
import yes_o from '../../Common/Images/ImagesForAlert/yes_o.png';
import no_n from '../../Common/Images/ImagesForAlert/no_n.png';
import no_o from '../../Common/Images/ImagesForAlert/no_o.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface StoreItem {
    StoreID: number;
    DateAdded: string;
    StoreTitle: string;
    ItemType: string;
    Price: number;
    Image: string;
    StartDate: string | null;
    EndDate: string | null;
    AllocatedTo: string;
    Purchased: number;
    NotSent: number;
    ItemsRemaining: number;
    MaxNumber: number;
    MaxPerStudent: number;
    DonationAmount: number;
    VouchersAvailable: number;
    VouchersSpent: number;
    TotalVouchers: number;
}

// --- Updated Styles for Fixed Layout ---
const styles = {
    container: {
        fontFamily: 'Arial',
        fontSize: '10pt',
        backgroundColor: '#ffffff',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column' as 'column',
        overflow: 'hidden'
    },
    titleBar: {
        color: 'White',
        backgroundColor: 'Maroon',
        padding: '5px',
        fontSize: '12pt',
        textAlign: 'center' as 'center',
        fontWeight: 'bold' as 'bold',
        flexShrink: 0
    },
    filterRow: {
        backgroundColor: '#ECFACB',
        padding: '4px',
        paddingLeft: '7px',
        fontSize: '11pt',
        fontWeight: 'bold' as 'bold',
        borderBottom: '1px solid #80a0ff',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0
    },
    tableContainer: {
        margin: '5px',
        overflowX: 'auto' as 'auto',
        overflowY: 'auto' as 'auto',
        flexGrow: 1
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        fontSize: '10pt',
        border: '1px solid #80a0ff',
        color: '#333333'
    },
    th: {
        backgroundColor: '#C7DDFD',
        color: '#2859ac',
        padding: '8px',
        fontWeight: 'normal' as 'normal',
        borderLeft: '1px solid #a3c0e8',
        bordercolor: 'inherit',
        textAlign: 'left' as 'left',
        whiteSpace: 'nowrap' as 'nowrap',
        position: 'sticky' as 'sticky',
        top: -1,
        zIndex: 1
    },
    td: {
        padding: '8px',
        borderRight: '1px solid #80a0ff',
        // borderBottom: "none",
        whiteSpace: 'nowrap' as 'nowrap',
        cursor: 'pointer'
    },

    selectedRow: {
        backgroundColor: 'orange',
        color: 'white'
    },
    footer: {
        padding: '10px',
        borderTop: '1px solid #80a0ff',
        backgroundColor: '#f9f9f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0          // Prevent shrinking
    },
    buttonContainer: {
        display: 'flex',
        gap: '5px',
        marginRight: '35%'
    },
    button: {
        padding: '4px 10px',
        cursor: 'pointer',
        fontSize: '10pt',
        fontFamily: 'Arial'
    },
    input: {
        padding: '2px',
        fontFamily: 'Arial',
        fontSize: '10pt'
    }
};

const EL_ManageStores: React.FC = () => {
    // --- State ---
    const [stores, setStores] = useState<StoreItem[]>([]);
    const [countries, setCountries] = useState<string[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('0');
    const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [showARD, setShowARD] = useState<boolean>(false);
    const [ardMode, setArdMode] = useState<'A' | 'R'>('A');
    const [showPurchases, setShowPurchases] = useState<boolean>(false);
    const [purchaseData, setPurchaseData] = useState<{
        title: string;
        endDate: string | null;
        totalPurchases: number;
    } | null>(null);
    const [showImportVouchers, setShowImportVouchers] = useState<boolean>(false);

    const [alertMsg, setAlertMsg] = useState('');
    const [confirmConfig, setConfirmConfig] = useState<{ msg: string, onConfirm: () => void } | null>(null);

    const handleShowWarning = (msg: string) => setAlertMsg(msg);

    const handleShowConfirm = (msg: string, onConfirm: () => void) => {
        setConfirmConfig({ msg, onConfirm });
    };

    const handleConfirmYes = () => {
        if (confirmConfig) confirmConfig.onConfirm();
        setConfirmConfig(null);
    };

    // --- Effects ---
    useEffect(() => {
        fetchCountries();
        // Removed duplicate fetchStores() call here
    }, []);

    useEffect(() => {
        fetchStores();
    }, [selectedCountry]);

    // --- API Calls ---
    const fetchCountries = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}api/manage-store/countries/`);
            if (!response.ok) throw new Error('Failed to fetch countries');
            const data = await response.json();
            setCountries(data);
        } catch (err: any) {
            console.error(err);
        }
    };

    const fetchStores = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}api/manage-store/?country=${selectedCountry}`);
            if (!response.ok) throw new Error('Failed to fetch stores');
            const data = await response.json();
            setStores(data);
            setSelectedStoreId(null);
        } catch (err: any) {
            setError('Error loading data.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedStoreId) return;

        handleShowConfirm("Do you want to remove the Store?", async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/manage-store/${selectedStoreId}/`, {
                    method: 'DELETE',
                });

                if (response.ok) {
                    setAlertMsg("Store removed successfully.");
                    fetchStores();
                } else {
                    handleShowWarning("Failed to delete store.");
                }
            } catch (err) {
                handleShowWarning("Error removing store.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        });
    };

    const WarningDialog = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: '400px', height: '200px', backgroundImage: `url(${alert_A_bg})`, backgroundRepeat: 'no-repeat', backgroundSize: '100% 100%', position: 'relative', fontFamily: 'Tahoma, Arial, sans-serif', fontSize: '12px', color: 'black' }}>
                <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>Warning</div>
                <div style={{ position: 'absolute', top: '40px', left: '100px', right: '15px', bottom: '50px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span style={{ fontWeight: 'normal', marginTop: '-25px', fontSize: '11pt' }}>{alertMsg}</span>
                </div>
                <div style={{ position: 'absolute', bottom: '10px', width: '100%', textAlign: 'center' }}>
                    <img src={ok_n} alt="OK" onClick={() => setAlertMsg('')} style={{ cursor: 'pointer' }} onMouseOver={(e) => e.currentTarget.src = ok_o} onMouseOut={(e) => e.currentTarget.src = ok_n} />
                </div>
            </div>
        </div>
    );

    const ConfirmDialog = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: '306px', height: '210px', backgroundImage: `url(${alert_bg})`, backgroundRepeat: 'no-repeat', position: 'relative', fontFamily: 'Tahoma', fontSize: '13px' }}>
                <div style={{ position: 'absolute', top: '5px', left: '0', width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '13px' }}>Confirmation</div>
                <div style={{ position: 'absolute', top: '35px', left: '70px', right: '10px', height: '110px', display: 'flex', alignItems: 'center', color: '#525252', fontSize: '11pt' }}>
                    {confirmConfig?.msg}
                </div>
                <div style={{ position: 'absolute', bottom: '15px', width: '100%', textAlign: 'center' }}>
                    <img src={yes_n} alt="Yes" onClick={handleConfirmYes} style={{ cursor: 'pointer', margin: '-10px 15px' }} onMouseOver={(e) => e.currentTarget.src = yes_o} onMouseOut={(e) => e.currentTarget.src = yes_n} />
                    <img src={no_n} alt="No" onClick={() => setConfirmConfig(null)} style={{ cursor: 'pointer', margin: '-10px 15px' }} onMouseOver={(e) => e.currentTarget.src = no_o} onMouseOut={(e) => e.currentTarget.src = no_n} />
                </div>
            </div>
        </div>
    );

    // --- Formatters ---
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatType = (type: string) => {
        if (type === 'G') return 'Game';
        if (type === 'I') return 'Item';
        return type;
    };

    const formatAllocated = (code: string) => {
        if (code === 'A') return 'All';
        if (code === 'F') return 'Home';
        if (code === 'S') return 'Schools';
        return code;
    };

    const handlePopupUpdate = (action: string) => {
        if (action === 'Add') {
            setArdMode('A');
            setShowARD(true);
        } else if (action === 'Modify') {
            if (selectedStoreId) {
                setArdMode('R');
                setShowARD(true);
            }
        } else if (action === 'ImportVouchers') {
            setShowImportVouchers(true);
        } else if (action === 'Purchases') {
            if (selectedStoreId) {
                const selectedStore = stores.find(s => s.StoreID === selectedStoreId);
                if (selectedStore) {
                    setPurchaseData({
                        title: selectedStore.StoreTitle,
                        endDate: selectedStore.EndDate,
                        totalPurchases: selectedStore.Purchased
                    });
                    setShowPurchases(true);
                }
            }
        }
    };

    const handlePopup = (action: string) => {
        if (!selectedStoreId) {
            setAlertMsg('Please select a store.');
            return;
        }
        if (action === 'Add') {
            setArdMode('A');
            setShowARD(true);
        } else if (action === 'Modify') {
            if (selectedStoreId) {
                setArdMode('R');
                setShowARD(true);
            }
        } else if (action === 'ImportVouchers') {
            setShowImportVouchers(true);
        } else if (action === 'Purchases') {
            if (selectedStoreId) {
                const selectedStore = stores.find(s => s.StoreID === selectedStoreId);
                if (selectedStore) {
                    setPurchaseData({
                        title: selectedStore.StoreTitle,
                        endDate: selectedStore.EndDate,
                        totalPurchases: selectedStore.Purchased
                    });
                    setShowPurchases(true);
                }
            }
        }
    };

    const handleSaveSuccess = () => {
        fetchStores();
    };

    return (
        <div style={styles.container}>
            {/* 1. Header (Fixed) */}
            <div style={styles.titleBar}>
                My Incentives Manager Store
            </div>

            {/* 2. Filter Bar (Fixed) */}
            <div style={styles.filterRow}>
                <span>Store</span>
                <select
                    style={{ ...styles.input, cursor: 'pointer' }}
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                >
                    <option value="0">--ALL--</option>
                    {countries.map((c, i) => (
                        <option key={i} value={c}>{c}</option>
                    ))}
                </select>
            </div>

            {/* 3. Data Grid (Scrollable Area) */}
            <div style={styles.tableContainer}>
                {loading && <Loading />}
                {error && <div style={{ padding: '10px', color: 'red' }}>{error}</div>}

                {!loading && !error && (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Date Added</th>
                                <th style={styles.th}>Title</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th}>Price</th>
                                <th style={styles.th}>Thumb</th>
                                <th style={styles.th}>Start Date</th>
                                <th style={styles.th}>End Date</th>
                                <th style={styles.th}>Allocated To</th>
                                <th style={styles.th}>Purchased</th>
                                <th style={styles.th}>NotSent</th>
                                <th style={styles.th}>Items Rem.</th>
                                <th style={styles.th}>Max Num</th>
                                <th style={styles.th}>Max/Student</th>
                                <th style={styles.th}>Store ID</th>
                                <th style={styles.th}>Donation</th>
                                <th style={styles.th}>Vouchers Avail</th>
                                <th style={styles.th}>Vouchers Spent</th>
                                <th style={styles.th}>Total Vouchers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stores.map((row, index) => (
                                <tr
                                    key={row.StoreID}
                                    style={{
                                        backgroundColor:
                                            selectedStoreId === row.StoreID
                                                ? styles.selectedRow.backgroundColor
                                                : index % 2 === 0
                                                    ? '#f4faff'   
                                                    : '#fff', 
                                        borderBottom: selectedStoreId === row.StoreID ? 'none' : undefined,
                                        
                                    }}
                                    onClick={() => setSelectedStoreId(row.StoreID)}
                                >
                                    <td style={styles.td}>{formatDate(row.DateAdded)}</td>
                                    <td style={styles.td}>{row.StoreTitle}</td>
                                    <td style={styles.td}>{formatType(row.ItemType)}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.Price}</td>
                                    <td style={styles.td}>{row.Image}</td>
                                    <td style={styles.td}>{formatDate(row.StartDate)}</td>
                                    <td style={styles.td}>{formatDate(row.EndDate)}</td>
                                    <td style={styles.td}>{formatAllocated(row.AllocatedTo)}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.Purchased}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.NotSent}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.ItemsRemaining}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.MaxNumber}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.MaxPerStudent}</td>
                                    <td style={styles.td}>{row.StoreID}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.DonationAmount}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.VouchersAvailable}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.VouchersSpent}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{row.TotalVouchers}</td>
                                </tr>
                            ))}
                            {stores.length === 0 && (
                                <tr>
                                    <td colSpan={18} style={{ ...styles.td, textAlign: 'center', padding: '20px' }}>
                                        No stores found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <div style={styles.footer}>
                <div>
                    <button style={styles.button} onClick={() => window.history.back()}>Back</button>
                </div>
                <div style={styles.buttonContainer}>
                    <button style={styles.button} onClick={() => handlePopupUpdate('Add')}>Add</button>
                    <button
                        style={styles.button}
                        disabled={!selectedStoreId}
                        onClick={() => handlePopup('Modify')}
                    >
                        View/Modify
                    </button>
                    <button
                        style={styles.button}
                        disabled={!selectedStoreId}
                        onClick={handleDelete}
                    >
                        Remove
                    </button>
                    <button
                        style={styles.button}
                        onClick={() => handlePopup('Purchases')}
                    >
                        View Purchases
                    </button>
                    <button style={styles.button} onClick={() => handlePopupUpdate('ImportVouchers')}>
                        Update Vouchers Purchased
                    </button>
                </div>
            </div>
            {showARD && (
                <EL_StoresARD
                    mode={ardMode}
                    storeID={selectedStoreId}
                    onClose={() => setShowARD(false)}
                    onSaveSuccess={handleSaveSuccess}
                    showWarning={handleShowWarning}
                    showConfirm={handleShowConfirm}
                />
            )}
            {showPurchases && selectedStoreId && purchaseData && (
                <EL_ManageStoresPurchases
                    storeID={selectedStoreId}
                    title={purchaseData.title}
                    endDate={purchaseData.endDate}
                    totalPurchases={purchaseData.totalPurchases}
                    onClose={() => setShowPurchases(false)}
                    showWarning={handleShowWarning}
                    showConfirm={handleShowConfirm}
                />
            )}
            {showImportVouchers && (
                <ImportVouchersPurchased onClose={() =>
                    setShowImportVouchers(false)}
                    showWarning={handleShowWarning}
                    showConfirm={handleShowConfirm} />
            )}
            {alertMsg && (
                <CustomAlert
                    message={alertMsg}
                    onClose={() => setAlertMsg('')}
                />
            )}
            {confirmConfig && <ConfirmDialog />}
        </div>
    );
};

export default EL_ManageStores;